import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  getSienaguardTokens,
  addSienaguardService,
  SienaguardService,
  SienaguardServiceDetail
} from '../../../src/api/sienaguardService';
import useSienaguard from '../../hooks/useSienaguard';

import StatsHeader from '../../components/Sienaguard/StatsHeader';
import AddServiceForm from '../../components/Sienaguard/AddServiceForm';
import ServicesTable from '../../components/Sienaguard/ServicesTable';
import ServiceDetail from '../../components/Sienaguard/ServiceDetail';
import ServiceDetailsDrawer from '../../components/Sienaguard/ServiceDetailsDrawer';
import BuyTokensModal from '../../components/Sienaguard/BuyTokensModal';

import Notification from '../../components/misc/Notification';
import Loader from '../../components/Loader';

import { useTranslation } from 'react-i18next';
import { FaCoins } from 'react-icons/fa';
import IPGlobalMap from '../../components/Sienaguard/IPGlobalMap';

const Sienaguard: React.FC = () => {
  const { t } = useTranslation();

  // Hook personalizado que trae la info de servicios
  const {
    services,
    serviceDetailsMap,
    loading,
    error,
    refreshSienaguard,
    refreshServiceDetailFor
  } = useSienaguard();

  // Tokens disponibles
  const [tokens, setTokens] = useState<number | null>(null);

  // Drawer lateral (info de un servicio)
  const [drawerService, setDrawerService] = useState<SienaguardServiceDetail | null>(null);

  // Servicio seleccionado en la tabla
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);

  // Notificaciones
  const [notificationMessage, setNotificationMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);

  // Modal para "comprar" o "añadir" tokens
  const [showBuyTokensModal, setShowBuyTokensModal] = useState(false);

  // Referencia al contenedor de ServiceDetail para hacer scroll
  const serviceDetailRef = useRef<HTMLDivElement>(null);

  // Muestra un mensaje emergente
  const showNotificationMessage = (message: string) => {
    setNotificationMessage(message);
    setShowNotification(true);
  };

  // Lógica para obtener tokens (evitamos warning con useCallback)
  const fetchTokens = useCallback(async () => {
    try {
      const fetchedTokens = await getSienaguardTokens();
      setTokens(fetchedTokens.sienaguard_tokens);
    } catch (err) {
      console.error(t('sienaguard.errorTokens'), err);
    }
  }, [t]);

  // Llamar al cargar el componente
  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  // Añadir un servicio
  const handleAddService = async (domain: string) => {
    try {
      const now = new Date();
      const oneYearLater = new Date(now);
      oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

      const startDate = now.toISOString().slice(0, 10);
      const endDate = oneYearLater.toISOString().slice(0, 10);

      await addSienaguardService(domain, startDate, endDate);

      // Actualizar tokens y tabla
      await fetchTokens();
      refreshSienaguard();

      showNotificationMessage(t('sienaguard.serviceAddedSuccess'));
    } catch (err) {
      console.error(t('sienaguard.errorAddService'), err);
    }
  };

  // Seleccionar fila en la tabla
  const handleSelectService = (serviceId: string) => {
    setSelectedServiceId(prev => (prev === serviceId ? null : serviceId));
    // Hacer scroll suave a ServiceDetail
    setTimeout(() => {
      serviceDetailRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  // Abrir Drawer
  const handleOpenDrawer = (service: SienaguardService) => {
    const detail = serviceDetailsMap[service.id];
    setDrawerService(detail);
  };

  // Cerrar Drawer
  const handleCloseDrawer = () => {
    setDrawerService(null);
  };

  // Al cambiar serviceDetailsMap, si el drawer está abierto, recargar
  useEffect(() => {
    if (drawerService) {
      const updated = serviceDetailsMap[drawerService.id];
      if (updated) {
        setDrawerService(updated);
      }
    }
  }, [drawerService, serviceDetailsMap]);

  // Refrescar detalle del drawer
  const handleRefreshDetail = async () => {
    if (drawerService) {
      await refreshServiceDetailFor(drawerService.id);
    }
  };

  // Servicio seleccionado
  const selectedServiceDetail = selectedServiceId
    ? serviceDetailsMap[selectedServiceId]
    : null;

  const allSuspicious = new Set<string>();

  services.forEach((svc) => {
    const detail = serviceDetailsMap[svc.id];
    if (!detail) return;
    
      // typosquatting
    detail.typosquatting_info?.forEach(t => {
      const dom = t.fake_domain.toLowerCase();
      if (dom !== svc.domain.toLowerCase()) {
        allSuspicious.add(dom);
      }
    });
    
    // names_tld
    detail.names_tld_info?.forEach(n => {
      const dom = n.domain.toLowerCase();
      if (dom !== svc.domain.toLowerCase()) {
        allSuspicious.add(dom);
      }
    });
  });
    
  const totalSuspicious = allSuspicious.size;

  if (loading) return <Loader />;
  if (error) return <p className="text-red-600">{error}</p>;

  const handleAddTokens = () => {
    setShowBuyTokensModal(true);
  };

  return (
    <div className="w-full px-8 py-8">

      {/* Título principal */}
      <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8 text-center">
        {t('sienaguard.title')}
      </h1>

      {/* Sección superior: tarjetas + boton tokens */}
      <div className="mb-8">
        <StatsHeader
          tokens={tokens ?? 0}
          totalDomains={services.length}
          totalSuspicious={totalSuspicious}
        />
      </div>

      <div className="mb-8 flex flex-col md:flex-row items-start gap-6">
        {/* Botón Añadir Tokens */}
        <div className="bg-gradient-to-b from-[#0A1723] to-[#050C12] text-white p-6 rounded-xl shadow flex flex-col items-center">
          <button
            onClick={handleAddTokens}
            className="bg-[#00FF69] text-black font-semibold px-4 py-2 rounded hover:bg-[#00e859] transition-colors flex items-center gap-2"
          >
            <FaCoins className="text-lg" />
            {t('sienaguard.addTokens')}
          </button>
        </div>

        {/* Formulario para "Analizar Ahora" */}
        <div className="flex-1">
          <AddServiceForm onSubmit={handleAddService} />
        </div>
      </div>

      {/* Tabla de servicios */}
      <ServicesTable
        services={services}
        serviceDetailsMap={serviceDetailsMap}
        onSelectService={handleSelectService}
        selectedServiceId={selectedServiceId}
        onOpenDrawer={handleOpenDrawer}
      />

      {/* Mapa global */}
      <IPGlobalMap
        services={services}
        serviceDetailsMap={serviceDetailsMap}
      />

      {/* ESPACIO EXTRA antes de ServiceDetail */}
      <div className="mt-12" ref={serviceDetailRef} />

      {/* Detalle de servicio (tabla "ciber" con runs) */}
      <ServiceDetail serviceDetail={selectedServiceDetail} />

      {/* Drawer lateral */}
      <ServiceDetailsDrawer
        service={drawerService}
        onClose={handleCloseDrawer}
        onRefresh={handleRefreshDetail}
        onNotify={showNotificationMessage}
      />

      {showNotification && (
        <Notification
          message={notificationMessage}
          onClose={() => setShowNotification(false)}
        />
      )}

      {showBuyTokensModal && (
        <BuyTokensModal
          isOpen={showBuyTokensModal}
          onClose={() => setShowBuyTokensModal(false)}
        />
      )}
    </div>
  );
};

export default Sienaguard;
